import React from 'react';
import { motion } from 'framer-motion';
import TopBar from '../global/TopBar';
import Applist from './Applist';
import { useProfilePhoto } from '../../context/ProfilePhotoContext';

const Home = ({ onNotificationAction }) => {
  // Use the profile photo context
  const { needsProfilePhoto, setShowProfilePhotoDialog } = useProfilePhoto();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark via-[#1a1c2e] to-sec-dark">
      <TopBar />
      
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Conditional profile completion reminder */}
        {needsProfilePhoto && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-lg bg-gradient-to-r from-soft-pink/10 to-mint-green/10 border border-white/10 shadow-lg"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Complete your profile</h3>
                <p className="text-sm text-white/70">Add a profile photo to personalize your experience</p>
              </div>
              <button 
                onClick={() => setShowProfilePhotoDialog(true)}
                className="px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-lg text-sm font-medium hover:shadow-md transition-all"
              >
                Upload Photo
              </button>
            </div>
          </motion.div>
        )}
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="relative"
        >
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
            <motion.div
              className="absolute top-0 left-0 w-96 h-96 bg-soft-pink rounded-full mix-blend-multiply filter blur-[128px] opacity-15"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
                x: [-50, 0, -50],
                y: [-50, 0, -50],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <motion.div
              className="absolute bottom-0 right-0 w-96 h-96 bg-mint-green rounded-full mix-blend-multiply filter blur-[128px] opacity-15"
              animate={{
                scale: [1.2, 1, 1.2],
                rotate: [90, 0, 90],
                x: [50, 0, 50],
                y: [50, 0, 50],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </div>

          {/* Hero Content */}
          <motion.div 
            variants={itemVariants}
            className="text-center mb-10 sm:mb-16 relative z-10 rounded-3xl"
          >
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 tracking-tight text-soft-pink rounded-3xl pt-4"
            >
              Welcome to Jingleez
            </motion.h1>


          </motion.div>

          {/* Main Content Card */}
          <motion.div
            variants={itemVariants}
            className="relative rounded-3xl overflow-hidden transform-gpu"
            style={{
              transform: 'perspective(1000px) rotateX(1deg)',
            }}
          >
            <div className="relative shadow-lg border border-white/5 bg-gradient-to-br from-pri-dark/90 to-sec-dark/95 rounded-2xl backdrop-blur-sm">
              {/* Top highlight */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent"></div>
              
              <div className="relative p-6">
                <motion.div
                  variants={itemVariants}
                  className="mb-6 text-center"
                >
                  <h2 className="text-2xl font-bold text-soft-pink mb-2">
                    Explore Our Services
                  </h2>
                  
                  <div className="h-0.5 w-16 bg-gradient-to-r from-soft-pink/50 to-mint-green/50 mx-auto mb-3"></div>
                  
                  <p className="text-pri-white/70 text-sm max-w-2xl mx-auto">
                    Access all your applications and services in one place
                  </p>
                </motion.div>
                
                <Applist />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default Home;
